import { z } from "zod";

// Base pricing interfaces
export interface BasePaymentOption {
  id: number;
  sortOrder?: number;
  planType: 'FULL_PAY' | 'DAILY' | 'BI_WEEKLY' | 'MONTHLY';
  displayName: string;
  paymentOptionName: string;     // Required - NEW FIELD from backend
  numberOfPayments: number;
  amount: number;
  frequency: 'ONCE' | 'DAILY' | 'BI_WEEKLY' | 'MONTHLY';
  squarePlanId?: string;
  youPay: number;
  youSave: string;
  paymentTags: string;
  cardTag: {
    available: boolean;
    value?: string;
  };
  slug?: string;
  currency: string;              // Required - default: 'USD'
}

export interface ProgramTypePricing {
  programType: 'hybrid' | 'via-zoom' | 'on-campus' | 'online';
  delivery: string;
  paymentOptions: BasePaymentOption[];
  programSchedule: ProgramSchedule;
  isConfigured: boolean; // Track if pricing was explicitly configured
  lastModified: Date;
  modifiedBy?: string;
}

export interface ProgramSchedule {
  hybrid?: {
    startMonth?: string[];
    startDate?: string[];
    clinicalLocations?: string[];
    practiceHours?: number;
  };
  'on-campus'?: {
    batches?: {
      schedule?: {
        batch?: string;
        date?: string;
        time?: string;
        capacity?: number;
      }[];
    }[];
    facilityRequirements?: string[];
  };
  'via-zoom'?: {
    batches?: {
      schedule?: {
        batch?: string;
        date?: string;
        time?: string;
        minParticipants?: number;
        maxParticipants?: number;
      }[];
    }[];
  };
  online?: {
    startMonth?: string[];
    startDate?: string[];
    enrollmentPeriods?: Array<{
      id: string;
      month: string;
      year: number;
      dates: string[];
      isActive: boolean;
    }>;
  };
}

export interface ModulePricing {
  moduleId: string;
  price: number;
  isActive: boolean;
  order: number;
  metadata?: {
    difficulty?: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';
    tags?: string[];
    estimatedDuration?: number;
  };
}

export interface ModuleConfiguration {
  modules: ModulePricing[];
  pricingStrategy: 'INDIVIDUAL' | 'BUNDLE';
  bundleDiscount: number;
  minimumModulesForBundle: number;
  moduleAccessType: 'SEQUENTIAL' | 'NON_SEQUENTIAL';
}

export interface PricingValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Zod schemas for validation
export const PaymentOptionSchema = z.object({
  id: z.number().positive(),
  sortOrder: z.number().int().positive().optional(),
  planType: z.enum(['FULL_PAY', 'DAILY', 'BI_WEEKLY', 'MONTHLY']),
  displayName: z.string().min(1).max(100),
  paymentOptionName: z.string().min(1).max(100),  // Required - NEW FIELD
  numberOfPayments: z.number().positive(),
  amount: z.number().min(0.01).max(100000),       // Updated with proper min/max
  frequency: z.enum(['ONCE', 'DAILY', 'BI_WEEKLY', 'MONTHLY']),
  squarePlanId: z.string().optional(),
  youPay: z.number().min(0.01),                   // Updated with proper min
  youSave: z.string(),
  paymentTags: z.string(),
  cardTag: z.object({
    available: z.boolean(),
    value: z.string().optional(),
  }),
  slug: z.string().optional(),
  currency: z.string().default('USD'),            // Required with default
});

export const ProgramTypePricingSchema = z.object({
  programType: z.enum(['hybrid', 'via-zoom', 'on-campus', 'online']),
  delivery: z.string(),
  paymentOptions: z.array(PaymentOptionSchema),
  programSchedule: z.object({
    hybrid: z.object({
      startMonth: z.array(z.string()),
      startDate: z.array(z.string()),
      clinicalLocations: z.array(z.string()).optional(),
      practiceHours: z.number().nonnegative().optional(),
    }).optional(),
    'on-campus': z.object({
      batches: z.array(z.object({
        schedule: z.array(z.object({
          batch: z.string(),
          date: z.string(),
          time: z.string(),
          capacity: z.number().nonnegative().optional(),
        })),
      })),
      facilityRequirements: z.array(z.string()),
    }).optional(),
    'via-zoom': z.object({
      batches: z.array(z.object({
        schedule: z.array(z.object({
          batch: z.string(),
          date: z.string(),
          time: z.string(),
          minParticipants: z.number().nonnegative().optional(),
          maxParticipants: z.number().nonnegative().optional(),
        })),
      })),
    }).optional(),
    online: z.object({
      startMonth: z.array(z.string()).optional(),
      startDate: z.array(z.string()).optional(),
      enrollmentPeriods: z.array(z.object({
        id: z.string(),
        month: z.string(),
        year: z.number(),
        dates: z.array(z.string()),
        isActive: z.boolean()
      })).optional(),
    }).optional(),
  }),
  isConfigured: z.boolean(),
  lastModified: z.date(),
  modifiedBy: z.string().optional(),
});

export const ModuleConfigurationSchema = z.object({
  modules: z.array(z.object({
    moduleId: z.string(),
    price: z.number().nonnegative(),
    isActive: z.boolean(),
    order: z.number().nonnegative(),
    metadata: z.object({
      difficulty: z.enum(['BEGINNER', 'INTERMEDIATE', 'ADVANCED']).optional(),
      tags: z.array(z.string()).optional(),
      estimatedDuration: z.number().optional(),
    }).optional(),
  })),
  pricingStrategy: z.enum(['INDIVIDUAL', 'BUNDLE']),
  bundleDiscount: z.number().min(0).max(100),
  minimumModulesForBundle: z.number().positive(),
  moduleAccessType: z.enum(['SEQUENTIAL', 'NON_SEQUENTIAL']),
});

// Business rule validation functions
export const validatePaymentOptions = (options: BasePaymentOption[]): PricingValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (options.length === 0) {
    errors.push('At least one payment option is required');
  }

  options.forEach((option, index) => {
    // Validate required fields
    if (!option.paymentOptionName?.trim()) {
      errors.push(`Payment option ${index + 1}: Payment option name is required`);
    }
    if (!option.displayName?.trim()) {
      errors.push(`Payment option ${index + 1}: Display name is required`);
    }

    // Validate amount constraints
    if (option.amount < 0.01) {
      errors.push(`Payment option ${index + 1}: Amount must be at least $0.01`);
    }
    if (option.amount > 100000) {
      errors.push(`Payment option ${index + 1}: Amount cannot exceed $100,000`);
    }

    // Validate youPay constraints
    if (option.youPay < 0.01) {
      errors.push(`Payment option ${index + 1}: You pay amount must be at least $0.01`);
    }
    if (option.youPay > option.amount * option.numberOfPayments) {
      errors.push(`Payment option ${index + 1}: You pay amount cannot exceed total payment amount`);
    }

    // Validate Square Plan ID for non-FULL_PAY plans
    if (option.planType !== 'FULL_PAY' && !option.squarePlanId?.trim()) {
      errors.push(`Payment option ${index + 1}: Square Plan ID is required for ${option.planType} plans`);
    }

    // Validate currency
    if (!option.currency || option.currency !== 'USD') {
      errors.push(`Payment option ${index + 1}: Currency must be USD`);
    }

    // Validate payment frequency matches plan type
    if (option.planType === 'FULL_PAY' && option.frequency !== 'ONCE') {
      errors.push(`Payment option ${index + 1}: Full pay plans must have ONCE frequency`);
    }
    if (option.planType === 'DAILY' && option.frequency !== 'DAILY') {
      errors.push(`Payment option ${index + 1}: Daily plans must have DAILY frequency`);
    }
    if (option.planType === 'BI_WEEKLY' && option.frequency !== 'BI_WEEKLY') {
      errors.push(`Payment option ${index + 1}: Bi-weekly plans must have BI_WEEKLY frequency`);
    }
    if (option.planType === 'MONTHLY' && option.frequency !== 'MONTHLY') {
      errors.push(`Payment option ${index + 1}: Monthly plans must have MONTHLY frequency`);
    }

    // Validate payment calculations
    const expectedTotal = option.amount * option.numberOfPayments;
    if (Math.abs(option.youPay - expectedTotal) > 0.01) {
      errors.push(`Payment option ${index + 1}: Total payment (${option.youPay}) doesn't match amount × payments (${expectedTotal})`);
    }

    // Validate savings calculation
    if (option.youSave && !option.youSave.includes('%')) {
      warnings.push(`Payment option ${index + 1}: Savings should be formatted as percentage`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

export const validateProgramTypePricing = (pricing: ProgramTypePricing): PricingValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate payment options
  const paymentValidation = validatePaymentOptions(pricing.paymentOptions);
  errors.push(...paymentValidation.errors);
  warnings.push(...paymentValidation.warnings);

  // Validate schedule based on program type - using nested structure
  if (pricing.programType === 'hybrid') {
    const scheduleData = pricing.programSchedule.hybrid;
    if (!scheduleData?.startMonth && !scheduleData?.startDate) {
      errors.push(`${pricing.programType} program type must have schedule configuration (startMonth or startDate)`);
    }
  }

  if (pricing.programType === 'online') {
    const scheduleData = pricing.programSchedule.online;
    if (!scheduleData?.startMonth?.length && !scheduleData?.startDate?.length) {
      errors.push(`${pricing.programType} program type must have schedule configuration (startMonth or startDate)`);
    }
  }

  if (pricing.programType === 'on-campus') {
    const scheduleData = pricing.programSchedule['on-campus'];
    if (!scheduleData?.batches || scheduleData.batches.length === 0) {
      errors.push(`${pricing.programType} program type must have schedule configuration (batches)`);
    }
  }

  if (pricing.programType === 'via-zoom') {
    const scheduleData = pricing.programSchedule['via-zoom'];
    if (!scheduleData?.batches || scheduleData.batches.length === 0) {
      errors.push(`${pricing.programType} program type must have schedule configuration (batches)`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

export const validateModuleConfiguration = (config: ModuleConfiguration): PricingValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (config.modules.length === 0) {
    errors.push('Module configuration must have at least one module');
  }

  if (config.pricingStrategy === 'BUNDLE') {
    if (config.bundleDiscount < 0 || config.bundleDiscount > 100) {
      errors.push('Bundle discount must be between 0 and 100 percent');
    }
    if (config.minimumModulesForBundle < 2) {
      errors.push('Bundle pricing requires at least 2 modules');
    }
    if (config.minimumModulesForBundle > config.modules.length) {
      errors.push('Minimum modules for bundle cannot exceed total modules');
    }
  }

  // Validate module prices
  config.modules.forEach((module, index) => {
    if (module.price < 0) {
      errors.push(`Module ${index + 1}: Price cannot be negative`);
    }
    if (module.order < 0) {
      errors.push(`Module ${index + 1}: Order cannot be negative`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};
